/**
 * TaskModalHeader - Header compacto do modal de tarefa
 * 
 * Layout horizontal com "Tarefa: #N — título" e ações.
 * Segue padrão do DealModalHeader (48px).
 * 
 * @version 1.2.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-07 v1.1.0 - Remover badges, exibir "Tarefa: #N — título" truncado
 * RELEASE: 2026-08-21 v1.2.0 - Nova ação "Alterar Fase" (desktop + menu mobile),
 *          exibida apenas com permissão granular can_change_phase na fase atual
 */
import {
  Pencil,
  Trash2,
  X,
  Play,
  Pause,
  CheckCircle2,
  XCircle,
  RotateCcw,
  MoreVertical,
  GitBranch,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import type { TaskDetail } from '@/hooks/useTaskDetails';

/**
 * Props do header
 */
/**
 * Permissões granulares por fase passadas pelo componente pai
 */
interface PhasePermissionsForHeader {
  canEdit: boolean;
  canDelete: boolean;
  canChangeStatus: boolean;
  canReopen: boolean;
  /** v1.2.0: permissão de mover a tarefa de fase */
  canChangePhase?: boolean;
}

interface TaskModalHeaderProps {
  task: TaskDetail;
  onEdit: () => void;
  onChangeStatus: (status: string) => void;
  onDelete: () => void;
  onClose: () => void;
  /** Callback para abrir dialog de motivo de pausa */
  onPause?: () => void;
  /** Callback para retomar tarefa */
  onResume?: () => void;
  /** Permissões granulares por fase (deny by default se não informado) */
  phasePermissions?: PhasePermissionsForHeader;
  /** v1.2.0: abre o modal de alteração de fase */
  onChangePhase?: () => void;
}

/**
 * Header compacto do modal (48px)
 */
export function TaskModalHeader({
  task,
  onEdit,
  onChangeStatus,
  onDelete,
  onClose,
  onPause,
  onResume,
  phasePermissions,
  onChangePhase,
}: TaskModalHeaderProps) {
  const isMobile = useIsMobile();
  const isClosed = task.status === 'completed' || task.status === 'cancelled';
  
  // Permissões por fase (deny by default se não informado)
  const perm = phasePermissions ?? {
    canEdit: false,
    canDelete: false,
    canChangeStatus: false,
    canReopen: false,
    canChangePhase: false,
  };

  /** Mapa de estilos de status para badge no header */
  /** Mapa de estilos de status para badge no header — inclui pending */
  // Estilo pill padronizado com a listagem de tarefas (TaskList)
  const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    pending: { label: 'Pendente', className: 'inline-flex items-center rounded-full font-medium bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-slate-500 before:mr-1.5' },
    in_progress: { label: 'Andamento', className: 'inline-flex items-center rounded-full font-medium bg-blue-50 text-blue-700 hover:bg-blue-50 dark:bg-blue-950/40 dark:text-blue-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-blue-500 before:mr-1.5' },
    paused: { label: 'Pausada', className: 'inline-flex items-center rounded-full font-medium bg-amber-50 text-amber-800 hover:bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-amber-500 before:mr-1.5' },
    completed: { label: 'Concluída', className: 'inline-flex items-center rounded-full font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-emerald-500 before:mr-1.5' },
    cancelled: { label: 'Cancelada', className: 'inline-flex items-center rounded-full font-medium bg-rose-50 text-rose-700 hover:bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-rose-500 before:mr-1.5' },
  };
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.in_progress;

  /** Monta itens de menu para DropdownMenu mobile */
  const mobileMenuItems = () => {
    const items: Array<{ label: string; icon: typeof Play; onClick: () => void; className?: string }> = [];

    // Ações de status
    if (task.status === 'pending' && perm.canChangeStatus) {
      items.push({ label: 'Iniciar', icon: Play, onClick: () => onChangeStatus('in_progress'), className: 'text-green-600' });
    }
    if (task.status === 'in_progress' && perm.canChangeStatus) {
      items.push({ label: 'Pausar', icon: Pause, onClick: () => onPause ? onPause() : onChangeStatus('paused'), className: 'text-yellow-600' });
      items.push({ label: 'Concluir', icon: CheckCircle2, onClick: () => onChangeStatus('completed'), className: 'text-green-600' });
      items.push({ label: 'Cancelar', icon: XCircle, onClick: () => onChangeStatus('cancelled'), className: 'text-destructive' });
    }
    if (task.status === 'paused' && perm.canChangeStatus) {
      items.push({ label: 'Retomar', icon: Play, onClick: () => onResume ? onResume() : onChangeStatus('in_progress'), className: 'text-blue-600' });
      items.push({ label: 'Concluir', icon: CheckCircle2, onClick: () => onChangeStatus('completed'), className: 'text-green-600' });
      items.push({ label: 'Cancelar', icon: XCircle, onClick: () => onChangeStatus('cancelled'), className: 'text-destructive' });
    }
    if (isClosed && perm.canReopen) {
      items.push({ label: 'Reabrir', icon: RotateCcw, onClick: () => onChangeStatus('in_progress') });
    }

    // v1.2.0: Alterar fase
    if (!isClosed && perm.canChangePhase && onChangePhase) {
      items.push({ label: 'Alterar Fase', icon: GitBranch, onClick: onChangePhase });
    }

    // Editar e excluir
    if (!isClosed && perm.canEdit) {
      items.push({ label: 'Editar', icon: Pencil, onClick: onEdit });
    }
    if (!isClosed && perm.canDelete) {
      items.push({ label: 'Excluir', icon: Trash2, onClick: onDelete, className: 'text-destructive' });
    }

    return items;
  };

  return (
    <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 h-12 border-b bg-background shrink-0">
      {/* Badge de status */}
      <Badge className={`text-[10px] h-5 px-2 shrink-0 ${statusConfig.className}`}>
        {statusConfig.label}
      </Badge>

      {/* Identificação da tarefa */}
      <span className="text-xs md:text-sm font-semibold text-muted-foreground shrink-0">
        #{task.number} —
      </span>

      {/* Título truncado */}
      <h2 className="text-xs md:text-sm font-semibold truncate flex-1 min-w-0">
        {task.title}
      </h2>

      {/* ═══ MOBILE: DropdownMenu compacto ═══ */}
      {isMobile ? (
        <div className="flex items-center gap-1 shrink-0 ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              {mobileMenuItems().map((item, idx) => (
                <DropdownMenuItem
                  key={idx}
                  onClick={item.onClick}
                  className={item.className}
                >
                  <item.icon className="h-3.5 w-3.5 mr-2" />
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Fechar */}
          <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        /* ═══ DESKTOP: botões individuais (layout original) ═══ */
        <div className="flex items-center gap-1 shrink-0 ml-auto">
          {/* Ação para status pendente — botão pill "Iniciar" (padrão referência) */}
          {task.status === 'pending' && perm.canChangeStatus && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 border-green-600/60 text-green-700 hover:text-green-800 hover:bg-green-50 hover:border-green-700 dark:text-green-500 dark:hover:text-green-400 dark:hover:bg-green-900/20"
              onClick={() => onChangeStatus('in_progress')}
            >
              <Play className="h-3.5 w-3.5" />
              <span className="font-medium">Iniciar</span>
            </Button>
          )}

          {/* Ações para em andamento */}
          {task.status === 'in_progress' && perm.canChangeStatus && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 border-yellow-600/60 text-yellow-700 hover:text-yellow-800 hover:bg-yellow-50 hover:border-yellow-700 dark:text-yellow-500 dark:hover:text-yellow-400 dark:hover:bg-yellow-900/20"
                onClick={() => onPause ? onPause() : onChangeStatus('paused')}
              >
                <Pause className="h-3.5 w-3.5" />
                <span className="font-medium">Pausar</span>
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20" onClick={() => onChangeStatus('completed')}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p className="text-xs">Concluir</p></TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => onChangeStatus('cancelled')}>
                    <XCircle className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p className="text-xs">Cancelar</p></TooltipContent>
              </Tooltip>
            </>
          )}
          {/* Ações para pausada */}
          {task.status === 'paused' && perm.canChangeStatus && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 border-blue-600/60 text-blue-700 hover:text-blue-800 hover:bg-blue-50 hover:border-blue-700 dark:text-blue-500 dark:hover:text-blue-400 dark:hover:bg-blue-900/20"
                onClick={() => onResume ? onResume() : onChangeStatus('in_progress')}
              >
                <Play className="h-3.5 w-3.5" />
                <span className="font-medium">Retomar</span>
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20" onClick={() => onChangeStatus('completed')}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p className="text-xs">Concluir</p></TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => onChangeStatus('cancelled')}>
                    <XCircle className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p className="text-xs">Cancelar</p></TooltipContent>
              </Tooltip>
            </>
          )}
          {/* Reabrir */}
          {isClosed && perm.canReopen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onChangeStatus('in_progress')}>
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><p className="text-xs">Reabrir</p></TooltipContent>
            </Tooltip>
          )}
          {/* Separador */}
          <div className="h-4 w-px bg-border mx-1" />

          {/* v1.2.0: Alterar Fase */}
          {!isClosed && perm.canChangePhase && onChangePhase && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onChangePhase}>
                  <GitBranch className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><p className="text-xs">Alterar Fase</p></TooltipContent>
            </Tooltip>
          )}

          {/* Editar */}
          {!isClosed && perm.canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><p className="text-xs">Editar</p></TooltipContent>
            </Tooltip>
          )}

          {/* Excluir */}
          {!isClosed && perm.canDelete && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={onDelete}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><p className="text-xs">Excluir</p></TooltipContent>
            </Tooltip>
          )}

          {/* Separador */}
          <div className="h-4 w-px bg-border mx-1" />

          {/* Fechar */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p className="text-xs">Fechar</p></TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
